import {createStyles, Select, Table} from '@mantine/core';
import React, {useMemo} from 'react';

export interface SchemaFieldRow {
  id: string;
  displayName: string;
}

export interface DatasetColumn {
  id: string;
  displayName: string;
}

export interface FieldToColumnMapperProps {
  rows: Array<SchemaFieldRow>;
  columns: Array<DatasetColumn>;
  /** row id -> column id. */
  mappings: Record<string, string>;
  onMappingChange: (mappings: Record<string, string>) => void;
  disabled?: boolean;
}

const useStyles = createStyles(() => ({
  // Fixed layout keeps the column dropdown's left edge aligned regardless of field name length.
  table: {
    tableLayout: 'fixed',
    width: '100%',
  },
  fieldNameCell: {
    width: '60%',
  },
  datasetColumnCell: {
    width: '40%',
  },
}));

export default function FieldToColumnMapper({
  rows,
  columns,
  mappings,
  onMappingChange,
  disabled,
}: FieldToColumnMapperProps): JSX.Element {
  const {classes} = useStyles();
  const columnData = useMemo(
    () => columns.map((column) => ({value: column.id, label: column.displayName})),
    [columns]
  );

  const handleChange = (rowId: string, columnId: string | null): void => {
    // Rebuild from the current rows so mappings for rows no longer present (e.g. removed or
    // now-computed schema fields) are dropped rather than silently re-persisted.
    const next: Record<string, string> = {};
    for (const row of rows) {
      const value = row.id === rowId ? columnId : mappings[row.id];
      if (value) {
        next[row.id] = value;
      }
    }
    onMappingChange(next);
  };

  return (
    <Table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.fieldNameCell}>Field</th>
          <th className={classes.datasetColumnCell}>Column</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td className={classes.fieldNameCell}>{row.displayName}</td>
            <td className={classes.datasetColumnCell}>
              <Select
                searchable={true}
                clearable={true}
                placeholder="Select a column..."
                aria-label={`Map schema field "${row.displayName}" to a dataset column`}
                clearButtonProps={{'aria-label': `Clear mapping for "${row.displayName}"`}}
                value={mappings[row.id] ?? null}
                disabled={disabled}
                nothingFound="No columns available"
                data={columnData}
                onChange={(value) => {
                  handleChange(row.id, value);
                }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
